import { CSSProperties, useEffect, useState } from "react";
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import { clsx, makeKey, styx } from "modules/Utils";
import { Body2 } from "components/shareable/Typography";
import { useDetectAdBlock } from "adblock-detect-react";
import Colors from "modules/Colors";
import ConnectionStep, { ConnectionStepProps } from 'pages/MarketingPage/Components/StepsSocialMedia/ConnectionStep'
import AccountContainer from 'components/AccountContainer'
import CardSymphony from "elements/CardSymphony";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";
import Icon from "elements/Icon";
import TabSymphony from "elements/TabSymphony";
import AdvertisingConnectionModalProps from "components/shareable/AdConnectionModal";
import { AdConnectionType, PlaybookType, SYMPHONY_WEBSITE_URL } from "modules/Const";
import useFacebookConnections from "Hooks/useFacebookConnections";
import FacebookIcon from "components/svg-icons/social/facebook";
import millify from "millify";
import { CURRENCY_CODE_DATA } from "helpers/currencies";
import useTikTokConnections from "Hooks/useTikTokConnections";
import ButtonSymphony from "elements/ButtonSymphony";
import Banner from "elements/Banner";
import DebugBox from "components/shareable/DebugBox";

const CONNECT_TABS = [
    {
        id: 'custom',
        title: 'Best Setup'
    },
    {
        id: 'express',
        title: 'Express Setup'
    },
]

interface ConnectionsCampaignViewProps {
    className?: string;
    style?: CSSProperties;
    variant?: 'facebook' | 'tiktok';
    allowExpressSetup?: boolean;
}

const ConnectionsCampaignView = ({
    className,
    style,
    variant = 'facebook',
    allowExpressSetup = false,
}: ConnectionsCampaignViewProps) => {
    const {
        expressSetupOrCustomPages,
        setExpressSetupOrCustomPages,
        conversionsEnabled,
        setConversionsEnabled,
        conversionsEventName,
        playbookType,
        linksToPromote,
        setConversionsEventName,
        getDataForSubmission
    } = useCampaignBuilder();

    const {
        loggedInFacebookUser,
        isLoggingIntoFacebook,
        loginWithFacebook,
        selectedFacebookAdAccount,
        selectedFacebookPixel,
        selectedFacebookPage,
        selectedInstagramPage,

        getConversionEvents,
        // selectedFB_page,
        // selectedInstaPage,
        // currency,
        // conversionsEnabled,
        // instagramPagesAvailable,
        // handleShowAdAccountModal,
        // handleShowFacebookModal,
        // handleShowInstagramModal,
        checkIfReadyToRunAds
    } = useFacebookConnections()

    const {
        loggedInUser: loggedInTikTokUser,
        isLoggedIn: isTikTokLoggedIn,
        isLoggingIn: isLoggingIntoTikTok,
        login: loginWithTikTok,
        tiktokAdAccount: selectedTikTokAdAccount,
        tiktokAdAccountFunded,
        refreshTikTokAdAccount,
    } = useTikTokConnections()

    const adBlockDetected = useDetectAdBlock();

    const [showAdConnectionsModal, setShowAdConnectionsModal] = useState(false)
    const [selectedAdConnectionType, setSelectedAdConnectionType] = useState<AdConnectionType | null>(null)

    useEffect(() => {
        // Check readiness whenever relevant connections change
        if (variant === 'facebook' && selectedFacebookPage && selectedFacebookAdAccount) {
            checkIfReadyToRunAds();
        } else if (variant === 'tiktok' && selectedTikTokAdAccount) {
            // TikTok readiness is determined by having a selected ad account and sufficient funds
            // No explicit check function needed
        }
    }, [
        selectedFacebookPage,
        selectedInstagramPage,
        selectedFacebookAdAccount,
        selectedTikTokAdAccount,
        variant,
    ]);
    /**
     * Manages conversion tracking based on pixel connection and URL type
     * - Enables conversions when a Facebook pixel is connected and a conversion event is selected
     * - For Symphony URLs, automatically enables conversions if a pixel is connected
     * - For non-Symphony URLs, disables conversions and clears the conversion event
     */
    useEffect(() => {
        // Always require a valid Facebook pixel for conversions
        if (!selectedFacebookPixel?.id) {
            setConversionsEnabled(false);
            return;
        }

        // Check if we have both a pixel and conversion event name
        if (selectedFacebookPixel && conversionsEventName) {
            setConversionsEnabled(true);
            return;
        }

        // Check if we have a URL that should have conversions enabled
        if (linksToPromote && linksToPromote.length > 0) {
            const currentUrl = linksToPromote[0]?.url || '';

            // Determine if this is a Symphony URL
            const isSymphonyUrl = Boolean(currentUrl) && (
                currentUrl.includes(SYMPHONY_WEBSITE_URL) ||
                currentUrl.includes('symphony.to')
            );

            // For Symphony URLs with a pixel, enable conversions
            if (isSymphonyUrl && selectedFacebookPixel) {
                setConversionsEnabled(true);
                return;
            }

            // For non-Symphony URLs, clear conversion event and disable
            if (!isSymphonyUrl && currentUrl) {
                setConversionsEventName('');
                setConversionsEnabled(false);
                return;
            }
        }

        // Default case - disable conversions
        setConversionsEnabled(false);
    }, [selectedFacebookPixel, conversionsEventName, linksToPromote]);

    useEffect(() => {
        if (selectedAdConnectionType) {
            setShowAdConnectionsModal(true)
        } else {
            setShowAdConnectionsModal(false)
        }
    }, [selectedAdConnectionType])

    const getTitleAndSubtitle = () => {
        switch (variant) {
            case 'facebook':
                return {
                    title: 'Connect your Advertising Accounts',
                    subtitle: "Symphony automatically runs ads on your behalf using your Facebook and Instagram accounts. Connect your accounts below to get started. You'll only have to do this once."
                }
            case 'tiktok':
                return {
                    title: 'Connect your Advertising Account',
                    subtitle: "Symphony automatically runs ads on your behalf using your TikTok for Business Account and Ad Account. Connect your account below to get started. You'll only have to do this once."
                }
        }
    }

    const renderTitleAndDescription = () => {
        const {
            title,
            subtitle
        } = getTitleAndSubtitle();

        return (
            <CardSymphony
                className="titleAndDescriptionConnectionsSetupHeader"
                title={title}
                subtitle={subtitle}
                isNoPadding
                size="small"
            >
                {adBlockDetected &&
                    <CardSymphony
                        className="adBlockDetectedContainer"
                        title="Please disable your ad blocker for the best experience."
                        subtitle="Symphony may not work correctly if you have an ad blocker enabled because we rely on advertising platforms like Facebook to automate marketing. Please disable your ad blocker to ensure your experience works properly."
                        isNoPadding
                        size="small"
                    />
                }
            </CardSymphony>
        )
    }

    /**
    * Renders the Facebook connection tabs UI component that allows users to choose between
    * Express Setup and Custom Setup options for connecting their Facebook advertising accounts.
    * 
    * @returns JSX.Element | null - Returns the tabs UI if express setup is allowed and required props exist,
    * otherwise returns null
    */
    const renderSetupTypeTabs = () => {
        if (!allowExpressSetup || !setExpressSetupOrCustomPages) {
            return null;
        }

        return (
            <TabsSymphony
                className="facebookTabs"
                style={{ marginBottom: 24 }}
                tabs={CONNECT_TABS}
                selectedTab={expressSetupOrCustomPages}
                onChange={(tab) => {
                    const tabItem = tab as TabItem;
                    setExpressSetupOrCustomPages(tabItem.id as 'custom' | 'express')
                }}
            />
        )
    };
    const renderBestSetup = () => {
        let connectionSteps: ConnectionStepProps[] = []
        switch (variant) {
            case 'facebook':
                let selectedAdAccountCurrency = selectedFacebookAdAccount?.currency || 'USD'
                let selectedAdAccountCurrencyName = CURRENCY_CODE_DATA[selectedAdAccountCurrency]?.name_plural || 'United States Dollar'
                connectionSteps = [
                    {
                        stepTitle: 'Facebook Profile',
                        stepIcon: <FacebookIcon />,
                        connectedAccountName: loggedInFacebookUser?.name || 'Facebook Profile',
                        connectedAccountIcon: <Icon
                            className="expressSetupStepIcon"
                            icon={loggedInFacebookUser?.picture?.data.url || 'facebook'}
                            iconType={loggedInFacebookUser ? "custom" : "default"}
                            size={40} />,
                        stepDescription: 'Connecting your Facebook profile lets Symphony automatically run ads using your selected page and ad account.',
                        isCompleted: Boolean(loggedInFacebookUser),
                        isLoading: isLoggingIntoFacebook,
                        onButtonClick: loginWithFacebook
                    },
                    {
                        stepTitle: 'Ad Account',
                        connectedAccountName: selectedFacebookAdAccount?.name || 'Connect your Ad Account',
                        connectedAccountIcon: <Icon
                            className="expressSetupStepIcon"
                            icon={'facebook'}
                            size={40} />,
                        stepDescription: 'Your Facebook ad account is where you want Symphony to publish your ads. You\'ll be billed based on the billing method in your ad account.',
                        isCompleted: Boolean(selectedFacebookAdAccount),
                        onButtonClick: () => {
                            setSelectedAdConnectionType(AdConnectionType.FACEBOOK_AD_ACCOUNT)
                        },
                        connectedAccountDescriptions: selectedFacebookAdAccount ? [
                            `$${millify(selectedFacebookAdAccount?.amount_spent || 0)} spent`,
                            `This ad account uses the currency ${selectedAdAccountCurrencyName} (${selectedAdAccountCurrency}).`
                        ] : undefined,
                        canEdit: true,
                        // canEdit: !conversionsEnabled,
                        // tooltip: conversionsEnabled ? "You can't edit this connection while conversions are enabled" : undefined
                    },
                    {
                        stepTitle: 'Facebook + Instagram Pages',
                        connectedAccountName: selectedFacebookPage?.name || 'Connect your Facebook Page',
                        connectedAccountIcon: <Icon
                            className="expressSetupStepIcon"
                            icon={selectedFacebookPage?.picture?.data.url || 'facebook'}
                            iconType={selectedFacebookPage ? 'custom' : 'default'}
                            size={40} />,
                        stepDescription: 'Select the Facebook Page and Instagram Page you want to use for your ads.',
                        isCompleted: Boolean(selectedFacebookPage),
                        onButtonClick: () => {
                            setSelectedAdConnectionType(AdConnectionType.FACEBOOK_PAGE)
                        },
                        canEdit: true,
                        iconContainerColor: Colors.grey,
                        isLastStep: !Boolean(selectedFacebookPage && selectedInstagramPage)
                    }
                ]

                if (selectedFacebookPage) {
                    connectionSteps.push({
                        isLastStep: Boolean(selectedFacebookPage && selectedInstagramPage),
                        stepTitle: 'Instagram Page',
                        connectedAccountName: selectedInstagramPage?.instagramAccount?.username || 'Connect your Instagram Page',
                        connectedAccountIcon: <Icon
                            className="expressSetupStepIcon"
                            icon={selectedInstagramPage?.instagramAccount?.profile_pic || 'instagram'}
                            iconType={selectedInstagramPage ? 'custom' : 'default'}
                            size={40} />,
                        stepDescription: 'This Instagram page will be the one seen by Instagram ad viewers. Your Instagram page needs to be connected to the Facebook page you\'ve linked above.',
                        isCompleted: Boolean(selectedInstagramPage),
                        onButtonClick: () => {
                            setSelectedAdConnectionType(AdConnectionType.FACEBOOK_PAGE)
                        }
                    })

                }


                let selectedConversionEventName = conversionsEventName
                let canEditConversionEvent = true
                let conversionEventIcon = 'facebook'
                let stepTitle = 'Conversion Event'
                let allowConversionEventStep = false
                let isConversionCompleted = Boolean(conversionsEventName)

                if (playbookType === PlaybookType.INCREASE_MUSIC_STREAMS ||
                    playbookType === PlaybookType.GROW_PLAYLIST_FOLLOWERS
                ) {
                    allowConversionEventStep = true
                    selectedConversionEventName = 'Ads will optimize to maximize streams & followers.'
                    canEditConversionEvent = false
                    conversionEventIcon = 'boost'
                    stepTitle = 'Conversion Event'
                    isConversionCompleted = true
                } else if (playbookType === PlaybookType.INCREASE_LINK_CLICKS) {
                    allowConversionEventStep = true
                } else if (playbookType === PlaybookType.META_PRESAVES) {
                    allowConversionEventStep = true
                    if (linksToPromote.length > 0) {
                        const isSymphonyLink = linksToPromote[0]?.isValidUrl && (linksToPromote[0]?.url.includes(SYMPHONY_WEBSITE_URL) || linksToPromote[0]?.url.includes('https://symphony.to'))
                        selectedConversionEventName = isSymphonyLink ? 'Ads will optimize to maximize pre-saves.' : undefined
                        canEditConversionEvent = isSymphonyLink ? false : true
                        isConversionCompleted = isSymphonyLink ? true : isConversionCompleted
                    } else {
                        canEditConversionEvent = true
                    }
                    conversionEventIcon = 'boost'
                    stepTitle = 'Pre-Save Conversion Event'
                } else if (playbookType === PlaybookType.META_EMAIL_SIGNUPS) {
                    allowConversionEventStep = true
                    if (linksToPromote.length > 0) {
                        const isSymphonyLink = linksToPromote[0]?.isValidUrl && (linksToPromote[0]?.url.includes(SYMPHONY_WEBSITE_URL) || linksToPromote[0]?.url.includes('https://symphony.to'))
                        canEditConversionEvent = isSymphonyLink ? false : true
                        selectedConversionEventName = isSymphonyLink ? 'Ads will optimize to grow signups.' : undefined
                        isConversionCompleted = isSymphonyLink ? true : isConversionCompleted
                    } else {
                        canEditConversionEvent = true
                    }
                    conversionEventIcon = 'mail'
                    stepTitle = 'Email Signup Conversion Event'
                } else if (playbookType === PlaybookType.META_TICKET_SALES) {
                    allowConversionEventStep = true
                    if (linksToPromote.length > 0) {
                        const isSymphonyLink = linksToPromote[0]?.isValidUrl && (linksToPromote[0]?.url.includes(SYMPHONY_WEBSITE_URL) || linksToPromote[0]?.url.includes('https://symphony.to'))
                        canEditConversionEvent = isSymphonyLink ? false : true
                        selectedConversionEventName = isSymphonyLink ? 'Ads will optimize to maximize ticket clicks.' : undefined
                        isConversionCompleted = isSymphonyLink ? true : isConversionCompleted
                    } else {
                        canEditConversionEvent = true
                    }
                    conversionEventIcon = 'metaTicketSalesV2'
                    stepTitle = 'Ticket Conversion Event'
                }

                if (allowConversionEventStep) {
                    // Support Conversions with simple Pixel connection
                    connectionSteps.push(
                        {
                            stepTitle: 'Enable Conversions for immediate impact',
                            connectedAccountName: selectedFacebookPixel?.name || 'Connect your Facebook Pixel',
                            connectedAccountIcon: <Icon
                                className="expressSetupStepIcon"
                                icon={'facebook'}
                                iconType={'default'}
                                size={40} />,
                            stepDescription: '(Optional) Improve your ad performance by adding your Facebook Pixel for better results.',
                            isCompleted: Boolean(selectedFacebookPixel && selectedFacebookPixel?.id),
                            onButtonClick: () => {
                                setSelectedAdConnectionType(AdConnectionType.FACEBOOK_PIXEL)
                            },
                            canEdit: true,
                            iconContainerColor: Colors.grey,
                            isLastStep: !Boolean(selectedFacebookPage && selectedInstagramPage)
                        }
                    )

                    // Add conversion event step only if pixel is connected
                    if (selectedFacebookPixel?.id) {
                        // Force conversion event name for specific playbook types
                        let displayName = selectedConversionEventName;

                        // Override for specific playbook types if it's a Symphony link
                        if (linksToPromote.length > 0) {
                            const isSymphonyLink = linksToPromote[0]?.isValidUrl &&
                                (linksToPromote[0]?.url.includes(SYMPHONY_WEBSITE_URL) ||
                                    linksToPromote[0]?.url.includes('https://symphony.to'));

                            if (isSymphonyLink) {
                                if (playbookType === PlaybookType.INCREASE_MUSIC_STREAMS ||
                                    playbookType === PlaybookType.GROW_PLAYLIST_FOLLOWERS) {
                                    displayName = 'Ads will optimize to maximize streams & followers.';
                                } else if (playbookType === PlaybookType.META_PRESAVES) {
                                    displayName = 'Ads will optimize to maximize pre-saves.';
                                } else if (playbookType === PlaybookType.META_EMAIL_SIGNUPS) {
                                    displayName = 'Ads will optimize to grow signups.';
                                } else if (playbookType === PlaybookType.META_TICKET_SALES) {
                                    displayName = 'Ads will optimize to maximize ticket clicks.';
                                }
                            }
                        }

                        connectionSteps.push({
                            stepTitle: stepTitle,
                            connectedAccountName: displayName || 'Select the Conversion Event',
                            connectedAccountIcon: <Icon
                                isCircle={!canEditConversionEvent}
                                className="expressSetupStepIcon"
                                icon={conversionEventIcon}
                                iconType={'default'}
                                size={40} />,
                            stepDescription: 'Choose which conversion event you want to optimize your ads for.',
                            isCompleted: isConversionCompleted,
                            onButtonClick: () => {
                                setSelectedAdConnectionType(AdConnectionType.FACEBOOK_CONVERSION_EVENT)
                            },
                            canEdit: canEditConversionEvent,
                            iconContainerColor: Colors.grey,
                            isLastStep: true
                        })
                    }
                }
                break
            case 'tiktok':
                let selectedTikTokCurrency = selectedTikTokAdAccount?.currency || 'USD'
                let selectedTikTokCurrencyName = CURRENCY_CODE_DATA[selectedTikTokCurrency]?.name_plural || 'United States Dollar'
                connectionSteps = [
                    {
                        stepTitle: 'TikTok Business Account',
                        connectedAccountName: loggedInTikTokUser?.display_name || 'TikTok Business Account',
                        connectedAccountIcon: <Icon
                            className="expressSetupStepIcon"
                            icon={loggedInTikTokUser?.avatar_url || 'tiktok'}
                            iconType={loggedInTikTokUser?.avatar_url ? 'custom' : 'default'}
                            size={40} />,
                        stepDescription: 'Connecting your TikTok for Business account lets Symphony automatically run ads using your selected ad account.',
                        isCompleted: Boolean(isTikTokLoggedIn),
                        isLoading: isLoggingIntoTikTok,
                        onButtonClick: loginWithTikTok
                    },
                    {
                        stepTitle: 'TikTok Ad Account',
                        connectedAccountName: selectedTikTokAdAccount?.name || 'Connect your TikTok Ad Account',
                        connectedAccountIcon: <Icon
                            className="expressSetupStepIcon"
                            icon={'tiktok'}
                            size={40} />,
                        stepDescription: 'Your TikTok ad account is where you want Symphony to publish your ads. You\'ll be billed based on the billing method in your ad account.',
                        isCompleted: Boolean(selectedTikTokAdAccount),
                        onButtonClick: () => {
                            setSelectedAdConnectionType(AdConnectionType.TIKTOK_AD_ACCOUNT)
                        },
                        connectedAccountDescriptions: selectedTikTokAdAccount ? [
                            `$${millify(Number(selectedTikTokAdAccount.balance) || 0)} Balance`,
                            `This ad account uses the currency ${selectedTikTokCurrencyName} (${selectedTikTokCurrency}).`
                        ] : undefined,
                        canEdit: true
                    },
                    {
                        stepTitle: 'Account Funding',
                        stepDescription: 'To ensure your ads run without interruptions, you\'ll need to ensure your TikTok Ad Account has funds. We recommend keeping at least $20 USD in your account.',
                        connectedAccountName: `Ad Account Spending Balance`,
                        connectedAccountIcon: <Icon
                            className="expressSetupStepIcon"
                            icon={'tiktok'}
                            size={40} />,
                        rightSideContent: selectedTikTokAdAccount ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Body2>
                                    ${millify(Number(selectedTikTokAdAccount.balance) || 0)}
                                </Body2>
                                <ButtonSymphony
                                    className="refreshBalanceButton"
                                    variant="text"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        refreshTikTokAdAccount(true);
                                    }}
                                >
                                    Refresh
                                </ButtonSymphony>
                            </div>
                        ) : undefined,
                        isCompleted: tiktokAdAccountFunded,
                        onButtonClick: () => {
                            if (selectedTikTokAdAccount) {
                                window.open(`https://business.tiktok.com/manage/payment/v2?org_id=${selectedTikTokAdAccount.owner_bc_id}`, '_blank');
                            }
                        },
                        canEdit: true,
                        isLastStep: true
                    }
                ]
                break
        }

        return (
            <div className="bestSetupContainer">
                {connectionSteps.map((step, index) => (
                    <ConnectionStep
                        key={makeKey(step.stepTitle, index)}
                        stepTitle={step.stepTitle}
                        connectedAccountName={step.connectedAccountName}
                        connectedAccountIcon={step.connectedAccountIcon}
                        stepDescription={step.stepDescription}
                        isCompleted={step.isCompleted}
                        onButtonClick={step.onButtonClick}
                        rightSideContent={step.rightSideContent}
                        stepIcon={step.stepIcon}
                        thumbnail={step.thumbnail}
                        iconContainerColor={step.iconContainerColor}
                        disabledButton={step.disabledButton}
                        errorText={step.errorText}
                        tooltip={step.tooltip}
                        canEdit={step.canEdit}
                        connectedAccountDescriptions={step.connectedAccountDescriptions}
                        isLoading={step.isLoading}
                        isLastStep={index === connectionSteps.length - 1}
                    >
                        {/* {step.renderContent && step.renderContent({
                            state: connectionState,
                            modals,
                            selectedCampaignType
                        })} */}
                    </ConnectionStep>
                ))}

                {/* <StepVideoContainer
                        // {...stepVideos.GETTING_STARTED}
                        // openModal={watchVideoModal.open}
                        // onCloseModal={watchVideoModal.closeModal}
                        // onOpenModal={watchVideoModal.openModal}
                        showDescription
                        hideThumbnail
                    /> */}

                <AdvertisingConnectionModalProps
                    isOpen={showAdConnectionsModal}
                    onClose={() => setSelectedAdConnectionType(null)}
                    connectionType={selectedAdConnectionType || undefined}
                />
            </div>
        )
    }

    const renderExpressSetup = () => {
        let description = ''
        let descriptions: string[] = []
        let subtitle = ''

        switch (variant) {
            case 'facebook':
                description = "With Express Setup, your ads will run directly through Symphony's Facebook Ad Account + associated FB + IG Page."
                descriptions = [
                    "Your ads will run through Symphony's FB + IG Pages",
                    "You'll get great results, but won't grow in IG followers"
                ]
                subtitle = "Login with Facebook to directly run ads with your own ad account, FB Page, and IG."
                break
            case 'tiktok':
                description = "With Express Setup, your ads will run directly through Symphony's TikTok Ad Account."
                descriptions = [
                    "Your ads will run through Symphony's TikTok Ad Account",
                    "You'll get great results, but won't grow in TikTok followers"
                ]
                subtitle = "Login with TikTok to directly run ads with your own ad account."
                break
        }

        let banner = null
        if (expressSetupOrCustomPages === 'express') {
            banner = (
                <Banner className="expressSetupBanner"
                    color="yellow"
                    style={{
                        marginTop: 8
                    }}>
                    <p>Your ads will run through Symphony's ad accounts. Your selected ad budget will be collected upfront.</p>
                </Banner>
            )
        }
        return (
            <>
                <div className="expressSetupContainer" style={{
                    ...sty.expressSetupContainer,
                    marginTop: 24
                }}>
                    <ConnectionStep
                        stepTitle="Express Setup"
                        stepDescription={description}
                        isCompleted
                        stepIcon="sLogo"
                        iconContainerColor={Colors.PAPER_LIGHT_GRAY_CONTAINER}
                        connectedAccountDescriptions={descriptions}
                        canEdit={false}
                        onButtonClick={() => { }}
                        isLastStep
                    >
                        <AccountContainer
                            className="accountContainerExpressSetup"
                            title="Want to run ads with your own pages?"
                            subtitle={subtitle}
                            iconImg={null}
                            buttonText="Use my own Pages →"
                            onClick={() => setExpressSetupOrCustomPages('custom')}
                        />
                    </ConnectionStep>
                </div>
                {banner}
            </>
        )
    }

    // NOTE: render()
    return (
        <div
            className={clsx("connectionsCampaignViewComponent", className)}
            style={styx(sty.connectionsCampaignViewComponent, style)}
        >
            {/* <DebugBox
                alwaysShow
                data={getDataForSubmission()}
            /> */}
            {renderTitleAndDescription()}
            {renderSetupTypeTabs()}
            <div className="connectionStepsContainer" style={{
                marginTop: 24
            }}>
                <TabSymphony className="customTab" visible={expressSetupOrCustomPages === 'custom'}>
                    {renderBestSetup()}
                </TabSymphony>
                <TabSymphony className="expressTab" visible={expressSetupOrCustomPages === 'express'}>
                    {renderExpressSetup()}
                </TabSymphony>
            </div>

        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    connectionsCampaignViewComponent: {
        width: '100%',
    },
    expressSetupContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16
    },
    stepper: {
        padding: 0
    },
    errorContainer: {
        display: 'flex',
        gap: 12,
        marginTop: 4,
        borderRadius: 8,
        padding: '16px 24px',
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: Colors.PAPER_ERROR_CONTAINER,
    },
    errorContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
}

export default ConnectionsCampaignView;